import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Plus, Trash2, Cog, Boxes, UserCog, Loader2, Bell, Truck, Pencil, X } from "lucide-react";

const inputCls = "w-full h-11 rounded-lg bg-slate-900 border border-slate-700 px-4 text-sm outline-none focus:border-amber-500 transition-colors";

function Section({ icon: Icon, title, children }) {
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/60 p-5 sm:p-6">
      <h2 className="font-heading text-lg font-bold mb-5 flex items-center gap-2">
        <Icon className="h-5 w-5 text-amber-400" /> {title}
      </h2>
      {children}
    </div>
  );
}

export default function Anagrafica() {
  const [tab, setTab] = useState("lines");
  const [lines, setLines] = useState([]);
  const [parts, setParts] = useState([]);
  const [operators, setOperators] = useState([]);

  const [lineForm, setLineForm] = useState({ name: "", code: "", description: "" });
  const [partForm, setPartForm] = useState({ name: "", code: "", description: "", standard_minutes: "" });
  const [opForm, setOpForm] = useState({ name: "", email: "", password: "", role: "operatore", warehouse_access: false });
  const [reminderDays, setReminderDays] = useState(3);
  const [suppliers, setSuppliers] = useState([]);
  const [supForm, setSupForm] = useState({ name: "", email: "", phone: "", notes: "" });
  const [editSupId, setEditSupId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [l, p, o, s, sup] = await Promise.all([api.get("/lines"), api.get("/parts"), api.get("/operators"), api.get("/settings"), api.get("/suppliers")]);
    setLines(l.data); setParts(p.data); setOperators(o.data); setReminderDays(s.data.reminder_days); setSuppliers(sup.data);
  };
  useEffect(() => { load(); }, []);

  const saveSupplier = async (e) => {
    e.preventDefault();
    if (!supForm.name || !supForm.email) return toast.error("Nome ed email obbligatori.");
    setBusy(true);
    try {
      if (editSupId) { await api.put(`/suppliers/${editSupId}`, supForm); toast.success("Fornitore aggiornato."); }
      else { await api.post("/suppliers", supForm); toast.success("Fornitore aggiunto."); }
      setSupForm({ name: "", email: "", phone: "", notes: "" }); setEditSupId(null); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const editSupplier = (s) => { setEditSupId(s.id); setSupForm({ name: s.name || "", email: s.email || "", phone: s.phone || "", notes: s.notes || "" }); };

  const saveSettings = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const { data } = await api.put("/settings", { reminder_days: Number(reminderDays) || 0 }); setReminderDays(data.reminder_days); toast.success("Impostazioni salvate."); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const addLine = async (e) => {
    e.preventDefault();
    if (!lineForm.name || !lineForm.code) return toast.error("Nome e codice obbligatori.");
    setBusy(true);
    try { await api.post("/lines", lineForm); setLineForm({ name: "", code: "", description: "" }); toast.success("Linea aggiunta."); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const addPart = async (e) => {
    e.preventDefault();
    if (!partForm.name || !partForm.code) return toast.error("Nome e codice obbligatori.");
    setBusy(true);
    try { await api.post("/parts", { ...partForm, standard_minutes: Number(partForm.standard_minutes) || 0 }); setPartForm({ name: "", code: "", description: "", standard_minutes: "" }); toast.success("Parte aggiunta."); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const addOp = async (e) => {
    e.preventDefault();
    if (!opForm.name || !opForm.email || !opForm.password) return toast.error("Compila tutti i campi.");
    setBusy(true);
    try { await api.post("/auth/register", opForm); setOpForm({ name: "", email: "", password: "", role: "operatore", warehouse_access: false }); toast.success("Operatore creato."); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const toggleWarehouse = async (id) => {
    await api.patch(`/operators/${id}/warehouse`);
    load();
  };

  const delItem = async (type, id) => {
    if (!window.confirm("Eliminare questo elemento?")) return;
    await api.delete(`/${type}/${id}`);
    toast.success("Eliminato."); load();
  };

  const tabs = [
    { id: "lines", label: "Linee", icon: Cog },
    { id: "parts", label: "Parti", icon: Boxes },
    { id: "operators", label: "Operatori", icon: UserCog },
    { id: "fornitori", label: "Fornitori", icon: Truck },
    { id: "avvisi", label: "Avvisi", icon: Bell },
  ];

  const submitBtn = (label) => (
    <button type="submit" disabled={busy} className="h-11 px-5 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-60">
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} {label}
    </button>
  );

  const listRow = (title, sub, onDel, testid) => (
    <div key={testid} data-testid={testid} className="flex items-center gap-3 py-3 border-b border-slate-800/60 last:border-0">
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{title}</div>
        {sub && <div className="text-xs text-slate-500 truncate">{sub}</div>}
      </div>
      {onDel && (
        <button onClick={onDel} className="p-2 rounded-lg text-slate-400 hover:text-rose-400 hover:bg-slate-800">
          <Trash2 className="h-4 w-4" />
        </button>
      )}
    </div>
  );

  return (
    <div className="space-y-6 animate-fade-up max-w-4xl">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Anagrafica</h1>
        <p className="text-slate-500 text-sm mt-1">Gestisci linee produttive, parti meccaniche e operatori.</p>
      </div>

      <div className="flex gap-2 border-b border-slate-800">
        {tabs.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === t.id ? "border-amber-500 text-amber-400" : "border-transparent text-slate-400 hover:text-slate-200"}`}>
            <t.icon className="h-4 w-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "lines" && (
        <Section icon={Cog} title="Linee di montaggio">
          <form onSubmit={addLine} className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-6">
            <input data-testid="line-name" className={`${inputCls} sm:col-span-2`} placeholder="Nome linea" value={lineForm.name} onChange={(e) => setLineForm({ ...lineForm, name: e.target.value })} />
            <input data-testid="line-code" className={inputCls} placeholder="Codice" value={lineForm.code} onChange={(e) => setLineForm({ ...lineForm, code: e.target.value })} />
            {submitBtn("Aggiungi")}
          </form>
          <div>{lines.map((l) => listRow(l.name, l.code, () => delItem("lines", l.id), `line-item-${l.id}`))}</div>
        </Section>
      )}

      {tab === "parts" && (
        <Section icon={Boxes} title="Parti / Componenti meccanici">
          <form onSubmit={addPart} className="grid grid-cols-1 sm:grid-cols-5 gap-3 mb-6">
            <input data-testid="part-name" className={`${inputCls} sm:col-span-2`} placeholder="Nome parte" value={partForm.name} onChange={(e) => setPartForm({ ...partForm, name: e.target.value })} />
            <input data-testid="part-code" className={inputCls} placeholder="Codice" value={partForm.code} onChange={(e) => setPartForm({ ...partForm, code: e.target.value })} />
            <input data-testid="part-standard" type="number" min="0" className={inputCls} placeholder="Tempo std (min)" value={partForm.standard_minutes} onChange={(e) => setPartForm({ ...partForm, standard_minutes: e.target.value })} />
            {submitBtn("Aggiungi")}
          </form>
          <div>{parts.map((p) => listRow(p.name, `${p.code} · Standard ${p.standard_minutes || 0} min`, () => delItem("parts", p.id), `part-item-${p.id}`))}</div>
        </Section>
      )}

      {tab === "operators" && (
        <Section icon={UserCog} title="Operatori & Responsabili">
          <form onSubmit={addOp} className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
            <input data-testid="op-name" className={inputCls} placeholder="Nome completo" value={opForm.name} onChange={(e) => setOpForm({ ...opForm, name: e.target.value })} />
            <input data-testid="op-email" type="email" className={inputCls} placeholder="Email" value={opForm.email} onChange={(e) => setOpForm({ ...opForm, email: e.target.value })} />
            <input data-testid="op-password" type="text" className={inputCls} placeholder="Password iniziale" value={opForm.password} onChange={(e) => setOpForm({ ...opForm, password: e.target.value })} />
            <select data-testid="op-role" className={inputCls} value={opForm.role} onChange={(e) => setOpForm({ ...opForm, role: e.target.value })}>
              <option value="operatore">Operatore</option>
              <option value="admin">Responsabile (Admin)</option>
            </select>
            <label className="sm:col-span-2 flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
              <input type="checkbox" data-testid="op-warehouse" checked={opForm.warehouse_access} onChange={(e) => setOpForm({ ...opForm, warehouse_access: e.target.checked })} className="h-4 w-4 accent-amber-500" />
              Consenti accesso al Magazzino
            </label>
            <div className="sm:col-span-2">{submitBtn("Crea operatore")}</div>
          </form>
          <div>
            {operators.map((o) => (
              <div key={o.id} data-testid={`op-item-${o.id}`} className="flex items-center gap-3 py-3 border-b border-slate-800/60 last:border-0">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{o.name} · {o.role === "admin" ? "Responsabile" : "Operatore"}</div>
                  <div className="text-xs text-slate-500 truncate">{o.email}</div>
                </div>
                <button onClick={() => toggleWarehouse(o.id)} disabled={o.role === "admin"} data-testid={`op-warehouse-toggle-${o.id}`} className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${o.role === "admin" || o.warehouse_access ? "bg-amber-500/10 text-amber-400 border-amber-500/30" : "bg-slate-800 text-slate-400 border-slate-700 hover:border-slate-500"}`}>
                  Magazzino {o.role === "admin" || o.warehouse_access ? "ON" : "OFF"}
                </button>
                <button onClick={() => delItem("operators", o.id)} className="p-2 rounded-lg text-slate-400 hover:text-rose-400 hover:bg-slate-800">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </Section>
      )}

      {tab === "fornitori" && (
        <Section icon={Truck} title="Fornitori">
          <form onSubmit={saveSupplier} className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-6">
            <input data-testid="sup-name" className={inputCls} placeholder="Nome fornitore" value={supForm.name} onChange={(e) => setSupForm({ ...supForm, name: e.target.value })} />
            <input data-testid="sup-email" type="email" className={inputCls} placeholder="Email ordini" value={supForm.email} onChange={(e) => setSupForm({ ...supForm, email: e.target.value })} />
            <input data-testid="sup-phone" className={inputCls} placeholder="Telefono (opz.)" value={supForm.phone} onChange={(e) => setSupForm({ ...supForm, phone: e.target.value })} />
            <div className="flex gap-2">
              {submitBtn(editSupId ? "Salva" : "Aggiungi")}
              {editSupId && <button type="button" onClick={() => { setEditSupId(null); setSupForm({ name: "", email: "", phone: "", notes: "" }); }} className="h-11 px-3 rounded-lg border border-slate-700 text-slate-400 hover:text-slate-200 text-sm flex items-center gap-1"><X className="h-3.5 w-3.5" /></button>}
            </div>
            <input data-testid="sup-notes" className={`${inputCls} sm:col-span-4`} placeholder="Note (opzionale)" value={supForm.notes} onChange={(e) => setSupForm({ ...supForm, notes: e.target.value })} />
          </form>
          <div>
            {suppliers.length === 0 && <p className="text-slate-500 text-sm">Nessun fornitore. Aggiungine uno per abilitare l'invio automatico degli ordini.</p>}
            {suppliers.map((s) => (
              <div key={s.id} data-testid={`sup-item-${s.id}`} className="flex items-center gap-3 py-3 border-b border-slate-800/60 last:border-0">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{s.name}</div>
                  <div className="text-xs text-slate-500 truncate">{s.email}{s.phone ? ` · ${s.phone}` : ""}</div>
                </div>
                <button onClick={() => editSupplier(s)} data-testid={`sup-edit-${s.id}`} className="p-2 rounded-lg text-slate-400 hover:text-amber-400 hover:bg-slate-800"><Pencil className="h-4 w-4" /></button>
                <button onClick={() => delItem("suppliers", s.id)} className="p-2 rounded-lg text-slate-400 hover:text-rose-400 hover:bg-slate-800"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        </Section>
      )}

      {tab === "avvisi" && (
        <Section icon={Bell} title="Avvisi di scadenza">
          <p className="text-sm text-slate-400 mb-4 max-w-lg">
            Ogni giorno alle 7:00 gli amministratori ricevono un'email di promemoria per le commesse in scadenza o già scadute. Scegli con quanti giorni di anticipo far partire l'avviso.
          </p>
          <form onSubmit={saveSettings} className="flex flex-wrap items-end gap-3">
            <div>
              <label className="text-xs font-mono-data uppercase tracking-widest text-slate-400">Giorni di anticipo</label>
              <input type="number" min="0" max="60" data-testid="reminder-days" className={`${inputCls} mt-2 w-40`} value={reminderDays} onChange={(e) => setReminderDays(e.target.value)} />
            </div>
            <div data-testid="reminder-save">{submitBtn("Salva impostazioni")}</div>
          </form>
          <p className="text-xs text-slate-600 mt-3">Attualmente: avviso {reminderDays} giorni prima della scadenza (0 = solo il giorno stesso e le scadute).</p>
        </Section>
      )}
    </div>
  );
}
